import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { defaultContent, type SiteContent } from "@/hooks/useContentStore";
import { API_ENDPOINTS } from "@/lib/api";



// ─── 用同域部署 API 读写，适合直接上传服务器部署 ────────────────────────

export async function fetchContentFromSupabase(): Promise<SiteContent | null> {
  try {
    const res = await fetch(API_ENDPOINTS.content, { headers: { Accept: "application/json" } });
    if (!res.ok) return null;
    const data = (await res.json()) as { content?: Partial<SiteContent> };
    if (!data.content) return null;
    return mergeWithDefault(data.content);
  } catch {
    return null;
  }
}

export async function pushContentToSupabase(content: SiteContent): Promise<boolean> {
  try {
    const res = await fetch(API_ENDPOINTS.content, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content }),
    });
    if (!res.ok) {
      console.error("[pushContentToSupabase] Error:", await res.text());
      return false;
    }
    const contentType = res.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) return false;
    const result = (await res.json()) as { success?: boolean };
    if (!result.success) return false;
    return true;
  } catch {
    return false;
  }
}

function isSameContent(a: SiteContent, b: SiteContent) {
  return JSON.stringify(a) === JSON.stringify(b);
}

function mergeWithDefault(saved: Partial<SiteContent>): SiteContent {
  const normalizedBgVolume = typeof saved.bgMusic?.volume === "number" && !Number.isNaN(saved.bgMusic.volume)
    ? Math.min(1, Math.max(0, saved.bgMusic.volume))
    : defaultContent.bgMusic.volume;
  const normalizedBgStrategy = saved.bgMusic?.strategy === "muted-fallback" || saved.bgMusic?.strategy === "manual"
    ? saved.bgMusic.strategy
    : defaultContent.bgMusic.strategy;

  return {
    ...defaultContent,
    ...saved,
    nav: saved.nav
      ? { ...defaultContent.nav, ...saved.nav, items: saved.nav.items ?? defaultContent.nav.items }
      : defaultContent.nav,
    bannerSlides: saved.bannerSlides ?? defaultContent.bannerSlides,
    products: saved.products ?? defaultContent.products,
    aboutStats: saved.aboutStats ?? defaultContent.aboutStats,
    contact: saved.contact
      ? { ...defaultContent.contact, ...saved.contact, infoItems: saved.contact.infoItems ?? defaultContent.contact.infoItems }
      : defaultContent.contact,
    productsDetail: saved.productsDetail ?? defaultContent.productsDetail,
    productCategories: saved.productCategories ?? defaultContent.productCategories,
    friendLinks: saved.friendLinks ?? defaultContent.friendLinks,
    socialLinks: saved.socialLinks ?? defaultContent.socialLinks,
    footerLegal: saved.footerLegal ? { ...defaultContent.footerLegal, ...saved.footerLegal } : defaultContent.footerLegal,
    techServiceDocs: saved.techServiceDocs ?? defaultContent.techServiceDocs,
    newsArticles: saved.newsArticles ?? defaultContent.newsArticles,
    aiChat: saved.aiChat
      ? {
          ...defaultContent.aiChat,
          ...saved.aiChat,
          enabled: typeof saved.aiChat.enabled === "boolean" ? saved.aiChat.enabled : defaultContent.aiChat.enabled,
          endpoint: saved.aiChat.endpoint?.trim() ?? defaultContent.aiChat.endpoint,
        }
      : defaultContent.aiChat,
    bgMusic: saved.bgMusic
      ? {
          ...defaultContent.bgMusic,
          ...saved.bgMusic,
          // 如果远端存的 url 是空的，保留 default url（本地文件）
          url: saved.bgMusic.url?.trim() || defaultContent.bgMusic.url,
          volume: normalizedBgVolume,
          strategy: normalizedBgStrategy,
        }
      : defaultContent.bgMusic,
  };
}

// ─── Context ──────────────────────────────────────────────────────────────

interface ContentContextValue {
  content: SiteContent;
  setContent: (c: SiteContent) => void;
  reload: () => Promise<void>;
}

const ContentContext = createContext<ContentContextValue>({
  content: defaultContent,
  setContent: () => {},
  reload: async () => {},
});

export function ContentProvider({ children }: { children: ReactNode }) {
  const [content, setContentState] = useState<SiteContent>(defaultContent);

  const reload = useCallback(async () => {
    const remote = await fetchContentFromSupabase();
    if (remote) {
      setContentState((prev) => (isSameContent(prev, remote) ? prev : { ...remote }));
    }
  }, []);

  const setContent = useCallback((c: SiteContent) => {
    setContentState({ ...c });
  }, []);

  // 首次挂载立即从 Supabase 拉
  useEffect(() => {
    reload();
  }, [reload]);

  // 每 30 秒轮询一次
  useEffect(() => {
    const timer = setInterval(reload, 30000);
    return () => clearInterval(timer);
  }, [reload]);

  // 切回标签页时立即重新拉
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) reload(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [reload]);

  // 同标签页 admin 保存后触发
  useEffect(() => {
    const onUpdate = () => reload();
    window.addEventListener("site-content-updated", onUpdate);
    return () => window.removeEventListener("site-content-updated", onUpdate);
  }, [reload]);

  return (
    <ContentContext.Provider value={{ content, setContent, reload }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useRemoteContent(): SiteContent {
  return useContext(ContentContext).content;
}

export function useContentContext() {
  return useContext(ContentContext);
}
